'use client'

import { Footer } from '@/components/layout/footer'
import { Header } from '@/components/layout/header'
import { useIsInPeriod } from '@/hooks/common/use-now'
import { isQrEnhancedMode } from '@/lib/utils/product/is-qr-enhanced-mode'
import { __cCartService } from '@/services/client/__c.cart.service'
import dynamic from 'next/dynamic'
import { usePathname, useSearchParams } from 'next/navigation'
import { useEffect, useState } from 'react'
import { GATrackingProvider } from './ga-tracking-provider'

/* -------- Dynamic imports -------- */
// Header/Footer import tĩnh có chủ đích: chúng render trên MỌI trang nên
// next/dynamic không tiết kiệm bundle, nhưng lại bọc component trong
// React.lazy + Suspense ở client trong khi server render thẳng — cây fiber
// hai bên lệch nhau làm mọi useId bên trong (Radix menu, Input) sinh id
// khác nhau → warning hydration attribute mismatch toàn site.
const BackToTop = dynamic(() =>
  import('@/components/ui/back-to-top').then((m) => m.BackToTop)
)
const ContactFab = dynamic(() =>
  import('@/components/ui/contact-fab').then((m) => m.ContactFab)
)
const StickySetting = dynamic(
  () =>
    import('@/components/layout/sticky-setting').then((m) => m.StickySetting),
  { ssr: false }
)
const StickyCoolClub = dynamic(
  () =>
    import('@/components/layout/sticky-coolclub').then((m) => m.StickyCoolClub),
  { ssr: false }
)
const StickyAIChat = dynamic(
  () =>
    import('@/components/layout/sticky-ai-chat').then((m) => m.StickyAIChat),
  { ssr: false }
)

/* -------- Helpers -------- */
const startsWith = (patterns: string[], path: string) =>
  patterns.some((p) => path.startsWith(p))

/* -------- Path rules -------- */
const NO_LAYOUT = [
  '/wireframe',
  // '/doi-tra',
  // '/en/doi-tra',
  '/hiep',
  '/lan',
  '/nhu',
  '/tri-an-coolclub-aeon-binh-tan',
  '/lookback',
  '/store-offline',
  '/cxp/embed',
  '/avatar-frame',
]
const NO_FOOTER = ['/checkout', '/cart', '/doi-tra']
const NO_STICKY = ['/cart', '/doi-tra']
const NO_TOPBAR = ['/cart', '/doi-tra']
const NO_NAVBAR: string[] = [] // keep if you still need it
const NO_PROMO = ['/cart', '/doi-tra']
const NO_GA = ['/auth/callback']

const stripLocale = (pathname: string) => {
  const localeRegex = /^\/(en|vi)(\/|$)/
  return pathname.replace(localeRegex, '/')
}

export const LayoutProvider = ({
  children,
  isMobile = false,
}: {
  children: React.ReactNode
  isMobile?: boolean
}) => {
  const isMobileOrTablet = isMobile
  const [isZaloEmbedded, setIsZaloEmbedded] = useState(false)

  useEffect(() => {
    const embedSource = document.cookie
      .split('; ')
      .find((row) => row.startsWith('embed-source='))
      ?.split('=')[1]
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsZaloEmbedded(embedSource === 'zalo')
  }, [])

  const pathname = usePathname()
  const cleanPath = stripLocale(pathname)
  const searchParams = useSearchParams()

  /* -------- Visibility flags -------- */
  const isZaloDoiTra = isZaloEmbedded && cleanPath.startsWith('/doi-tra')
  // Layout QR sản phẩm (mobile): ẩn BackToTop + chat Zalo xuyên suốt, header/footer
  // vẫn giữ nguyên — xem isQrEnhancedMode.
  const isQrProductLayout =
    isMobileOrTablet &&
    cleanPath.startsWith('/product/') &&
    isQrEnhancedMode(searchParams)
  const showLayout = !startsWith(NO_LAYOUT, cleanPath) && !isZaloDoiTra
  const showFooter = !startsWith(NO_FOOTER, cleanPath)
  const showSticky = !startsWith(NO_STICKY, cleanPath) && !isQrProductLayout
  const showTopBar = !startsWith(NO_TOPBAR, cleanPath)
  const showNavbar = !startsWith(NO_NAVBAR, cleanPath)

  // useIsInPeriod trả false ở SSR + render hydrate đầu tiên để layout
  // server và client luôn khớp nhau (tránh React #418 toàn site mùa promo)
  const isPromoPeriod = useIsInPeriod(
    new Date('2026-02-02'),
    new Date('2026-02-22')
  )

  const showPromo =
    !startsWith(NO_PROMO, cleanPath) ||
    (cleanPath === '/cart' && isPromoPeriod && !isMobileOrTablet)
  const showGA = !startsWith(NO_GA, cleanPath)

  useEffect(() => {
    window.addToCart = __cCartService.addToCart
  }, [])

  return (
    <GATrackingProvider shouldShowGATracking={showGA}>
      {showLayout ? (
        <>
          <Header
            isShowTopBar={showTopBar}
            isShowNavbar={showNavbar}
            isShowPromotion={showPromo}
            isMobile={isMobileOrTablet}
            isQrGalleryLayout={isQrProductLayout}
          />
          <main id='main-content' className='min-h-screen'>
            {children}
          </main>

          {showFooter && <Footer />}
          {showSticky && (
            <>
              <BackToTop />
              {/* <ContactFab /> */}
              <StickyAIChat />
            </>
          )}

          {/* always render these two (client-only) sticky helpers */}
          <StickySetting />
          <StickyCoolClub />
        </>
      ) : (
        <>{children}</>
      )}
    </GATrackingProvider>
  )
}
